import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatedBoard } from './AnimatedBoard';
import { Footer } from './Footer';
import './LandingPage.css';

export function LandingPage() {
  const [loading, setLoading] = useState(false);
  const [loadingBot, setLoadingBot] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleChallenge = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/games', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to create game');
      }

      const data = await response.json();
      // Navigate directly to the game page - will show waiting room
      navigate(`/game/${data.gameId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
    }
  };

  const handleChallengeBot = async () => {
    setLoadingBot(true);
    setError(null);

    try {
      const response = await fetch('/api/games/bot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to create bot game');
      }

      const data = await response.json();
      // Navigate directly to the game - no waiting for opponent
      navigate(`/game/${data.gameId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoadingBot(false);
    }
  };

  return (
    <div className="landing-page">
        <div className="landing-container">
        <header className="landing-header">
          <div className="header-content">
            <div className="header-logo">
              <img 
                src="/jester-chess-logo.png" 
                alt="Jester Chess" 
                className="logo-image"
              />
            </div>
            <div className="button-group">
              <button 
                className="challenge-button-header" 
                onClick={handleChallenge}
                disabled={loading || loadingBot}
              >
                {loading ? 'Creating...' : '👥 Challenge a Friend!'}
              </button>
              <button 
                className="challenge-bot-button" 
                onClick={handleChallengeBot}
                disabled={loading || loadingBot}
              >
                {loadingBot ? 'Starting...' : '🤖 Challenge a Bot!'}
              </button>
            </div>
          </div>
          {error && <p className="error-message">{error}</p>}
        </header>

        <div className="about-section">
          <div className="about-content">
            <div className="about-text">
              <h2>Welcome to Jester Chess</h2>
              <p className="intro-text">
                Jester Chess is an innovative online chess variant that combines classic chess strategy with unpredictable 
                random elements. Experience the thrill of traditional chess while navigating the chaos introduced by the 
                jester piece—a wildcard that can change the game in an instant.
              </p>
              <p className="intro-text">
                Whether you're a chess master or a casual player, Jester Chess offers a fresh and exciting challenge. 
                The jester piece adds an element of chance that keeps every game unique and unpredictable, making each 
                match a new adventure.
              </p>
            </div>
            <div className="about-board">
              <AnimatedBoard size={350} />
            </div>
          </div>
        </div>

        <div className="rules-section">
          <h2 className="rules-title">How to Play Jester Chess</h2>
          
          <div className="rules-grid">
            <section className="rule-block">
              <div className="rule-icon">🎭</div>
              <h3 className="rule-heading">The Jester Piece</h3>
              <ul className="rule-list">
                <li>Each player has an extra piece, which is essentially a <strong>checker</strong>.</li>
                <li>These checkers act as <strong>"jesters"</strong> or <strong>"fools"</strong>.</li>
                <li>The <strong>white jester</strong> starts on the <strong>D4</strong> square.</li>
                <li>The <strong>black jester</strong> starts on the <strong>E5</strong> square.</li>
                <li>Jesters <strong>cannot be captured</strong> by any other pieces on the board.</li>
                <li>Jesters are capable of <strong>capturing any piece</strong>, including a player's own pieces.</li>
                <li>A jester can only be captured by the <strong>opposing jester</strong>.</li>
              </ul>
            </section>

            <section className="rule-block">
              <div className="rule-icon">🎲</div>
              <h3 className="rule-heading">Jester Movement</h3>
              <ul className="rule-list">
                <li>The jester's movement is <strong>completely random</strong>.</li>
                <li>To move a jester, a player rolls an <strong>8-sided die twice</strong>.</li>
                <li>The <strong>first roll</strong> indicates the <strong>file (column)</strong> to which the jester moves.</li>
                <li>The <strong>second roll</strong> indicates the <strong>rank (row)</strong> to which the jester moves.</li>
                <li className="rule-example">
                  <strong>Example:</strong> If a player rolls a <strong>3</strong> and then a <strong>6</strong>, the jester moves to the <strong>C6</strong> square.
                </li>
              </ul>
            </section>

            <section className="rule-block">
              <div className="rule-icon">⚔️</div>
              <h3 className="rule-heading">Capture Rules</h3>
              <ul className="rule-list">
                <li>
                  <strong>Capture Rule:</strong> If a jester lands on a square already occupied by another piece (whether it belongs to the player or their opponent), that piece is captured.
                </li>
                <li className="rule-special rule-win">
                  <strong>Instant Win Condition:</strong> If a jester lands on a square occupied by the opponent's king, the player instantly wins the game.
                </li>
                <li className="rule-special rule-loss">
                  <strong>Instant Loss Condition:</strong> If a jester lands on a player's own king, the player instantly loses the game.
                </li>
              </ul>
            </section>

            <section className="rule-block">
              <div className="rule-icon">♟️</div>
              <h3 className="rule-heading">Checkmate Escape</h3>
              <ul className="rule-list">
                <li>
                  <strong>Checkmate Escape Rule:</strong> If a player checkmates their opponent, the opponent <em>must</em> roll to move their jester (if it's still on the board).
                  <ul className="rule-sublist">
                    <li>If the jester lands on a square that effectively nullifies the checkmate, the game continues normally.</li>
                    <li>If it doesn't, then the game ends (presumably with the checkmate standing).</li>
                  </ul>
                </li>
              </ul>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

